/**
 * RTK Query API endpoints for user management
 *
 * Provides:
 * - List users with pagination and filtering
 * - Get user details by ID
 * - Create new user (local or remote flow)
 * - Update existing user
 * - Delete user (soft-delete)
 * - Reset password
 * - Verify email (public endpoint)
 *
 * @module Features/UserManagement/Service
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../Api/baseQueryWithReauth';
import {
  UserListDto,
  UserDetailsDto,
  CreateUserRequest,
  CreateUserResponse,
  UpdateUserRequest,
  ResetPasswordRequest,
  ResetPasswordResponse,
  VerifyEmailRequest,
  VerificationResult,
  PagedResult,
  ListUsersParams,
} from '../Types';

import { resolveAuthApiUrl } from '../../../Api/apiUrl';

const AUTH_API_URL = resolveAuthApiUrl(process.env.REACT_APP_API_URL);

/**
 * Tag types for cache invalidation
 */
enum TagTypes {
  USER_LIST = 'USER_LIST',
  USER_DETAILS = 'USER_DETAILS',
}

/**
 * RTK Query API for user management
 * Automatically handles JWT token refresh via baseQuery
 */
export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [TagTypes.USER_LIST, TagTypes.USER_DETAILS],

  endpoints: (builder) => ({
    /**
     * List all users with pagination and filtering
     * @query {page, pageSize, searchEmail?, role?, isActive?, emailConfirmed?}
     * @returns {PagedResult<UserListDto>}
     */
    listUsers: builder.query<PagedResult<UserListDto>, ListUsersParams>({
      query: ({ page, pageSize, searchEmail, role, isActive, emailConfirmed }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          pageSize: pageSize.toString(),
        });

        if (searchEmail) params.append('searchEmail', searchEmail);
        if (role) params.append('role', role);
        if (isActive !== null && isActive !== undefined) {
          params.append('isActive', isActive.toString());
        }
        if (emailConfirmed !== null && emailConfirmed !== undefined) {
          params.append('emailConfirmed', emailConfirmed.toString());
        }

        return `/users?${params.toString()}`;
      },
      providesTags: [TagTypes.USER_LIST],
    }),

    /**
     * Get detailed information about a specific user
     * @query {id}
     * @returns {UserDetailsDto}
     */
    getUserById: builder.query<UserDetailsDto, number>({
      query: (id) => `/users/${id}`,
      providesTags: (result, error, id) => [{ type: TagTypes.USER_DETAILS, id }],
    }),

    /**
     * Create new user with optional email verification
     * Supports both local (active immediately) and remote (verification required) flows
     *
     * @mutation
     * @param {CreateUserRequest} request - User creation details
     * @returns {CreateUserResponse} - New user ID and temporary password (if local flow)
     *
     * @example
     * // Local employee (no verification):
     * mutation {
     *   email: "john@agency.com",
     *   firstName: "John",
     *   lastName: "Doe",
     *   roles: ["Clerk"],
     *   requireEmailVerification: false  // User active immediately
     * }
     *
     * @example
     * // Remote employee (with verification):
     * mutation {
     *   email: "remote@agency.com",
     *   firstName: "Remote",
     *   lastName: "Employee",
     *   roles: ["Admin"],
     *   requireEmailVerification: true  // User inactive until email verified
     * }
     */
    createUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
      query: (body) => ({
        url: '/users',
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.USER_LIST],
    }),

    /**
     * Update existing user
     * Can change email, name, roles, status, and unlock account
     *
     * @mutation
     * @param {number} id - User ID
     * @param {UpdateUserRequest} request - Updated user data
     * @returns {UserDetailsDto} - Updated user details
     */
    updateUser: builder.mutation<UserDetailsDto, { id: number; body: UpdateUserRequest }>({
      query: ({ id, body }) => ({
        url: `/users/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        TagTypes.USER_LIST,
        { type: TagTypes.USER_DETAILS, id },
      ],
    }),

    /**
     * Delete (soft-delete) a user
     * User is deactivated but data is retained for audit
     *
     * @mutation
     * @param {number} id - User ID to delete
     */
    deleteUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypes.USER_LIST],
    }),

    /**
     * Reset user password
     * Generates new temporary password and optionally sends via email
     *
     * @mutation
     * @param {number} id - User ID
     * @param {ResetPasswordRequest} request - Reset options (sendEmail, etc.)
     * @returns {ResetPasswordResponse} - New temporary password
     */
    resetPassword: builder.mutation<
      ResetPasswordResponse,
      { id: number; body: ResetPasswordRequest }
    >({
      query: ({ id, body }) => ({
        url: `/users/${id}/reset-password`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: TagTypes.USER_DETAILS, id }],
    }),

    /**
     * Verify user email (public endpoint, no auth required)
     * Called when user clicks activation link in email
     *
     * @mutation
     * @param {VerifyEmailRequest} request - User ID and verification token
     * @returns {VerificationResult} - Success status and message
     *
     * @example
     * // Called via URL: /auth/verify-email?userId=2&token=abc123
     * mutation {
     *   userId: 2,
     *   token: "abc123..."
     * }
     */
    verifyEmail: builder.mutation<VerificationResult, VerifyEmailRequest>({
      query: (body) => ({
        url: `${AUTH_API_URL}/auth/verify-email`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

/**
 * Exported RTK Query hooks
 * Use these in components to fetch/mutate user data
 *
 * @example
 * const { data, isLoading, error } = useListUsersQuery({
 *   page: 1,
 *   pageSize: 10,
 *   searchEmail: 'john@'
 * });
 */
export const {
  useListUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
} = usersApi;
